import {
  IsUUID,
  IsString,
  IsOptional,
  IsNumber,
  ValidateNested,
  IsEnum,
  IsArray,
  Min,
  IsDateString,
} from 'class-validator';
import { Type } from 'class-transformer';

import {
  ProposalsResponseInterface,
  ProposalAttachmentsResponseInterface,
  ProposalItemsResponseInterface,
  ProposalPricingSummaryResponseInterface,
  ProposalAttachmentsStatus,
  ProposalItemStatus,
  ProposalPricingSummaryStatus,
  ProposalStatus,
  ProposalCompanies,
  ProposalPaymentTerms,
  ProposalValidity,
  ProposalWarranty,
} from '../../interface/proposals/proposals-response.interface';

import { AttachmentsDto } from '../attachments/attachments.dto';
import { MiniUsersDto } from '../users/users.dto';
import { CompanyDto } from '../companies/company.dto';
import { Brand, CategoryTypes, ProductCategory } from '../../utils/enums.utils';

export class ProposalAttachmentsDto implements ProposalAttachmentsResponseInterface {

  @IsUUID()
  proposal_id!: string;

  @ValidateNested()
  @Type(() => AttachmentsDto)
  attachment!: AttachmentsDto;

  @IsEnum(ProposalAttachmentsStatus)
  status!: ProposalAttachmentsStatus;
}

export class ProposalItemsDto implements ProposalItemsResponseInterface {

  @IsUUID()
  id!: string;

  @IsOptional()
  @IsString()
  item_details!: string | null;

  @IsOptional()
  @IsString()
  description!: string | null;

  @IsOptional()
  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  @Min(0)
  quantity!: number | null;

  @IsOptional()
  @IsString()
  uom!: string | null;

  @IsOptional()
  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  buying_vat_inc!: number | null;

  @IsOptional()
  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  total_buying!: number | null;

  @IsOptional()
  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  shipping_fee!: number | null;

  @IsOptional()
  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  wsf!: number | null;

  @IsOptional()
  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  markup!: number | null;

  @IsOptional()
  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  sp!: number | null;

  @IsOptional()
  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  sp_each!: number | null;

  @IsOptional()
  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  misc!: number | null;

  @IsOptional()
  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  tpc_price!: number | null;

  @IsOptional()
  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  fsp!: number | null;

  @IsOptional()
  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  fsp_each!: number | null;

  @IsOptional()
  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  profit_each!: number | null;

  @IsOptional()
  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  total_profit!: number | null;

  @IsOptional()
  @IsString()
  delivery!: string | null;

  @IsOptional()
  @IsString()
  project!: string | null;

  @IsEnum(Brand)
  brand!: Brand;

  @IsUUID()
  proposal_id!: string;

  @IsOptional()
  @ValidateNested()
  @Type(() => AttachmentsDto)
  attachment_item_img!: AttachmentsDto | null;

  @IsEnum(ProposalItemStatus)
  status!: ProposalItemStatus;

  @IsOptional()
  @IsString()
  lof!: string | null;

  @IsOptional()
  @IsString()
  aof!: string | null;

  @IsOptional()
  @IsString()
  srf!: string | null;
  
  @IsOptional()
  @IsString()
  lsf!: string | null;
}

export class ProposalPricingSummaryDto implements ProposalPricingSummaryResponseInterface {

  @IsUUID()
  id!: string;

  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  subtotal!: number;

  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  shipping_fee!: number;

  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  markup!: number;

  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  misc!: number;

  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  approval_discount!: number;

  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  discount_percent!: number;

  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  discount_amount!: number;

  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  total_ex_vat!: number;

  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  total_inc_vat!: number;

  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  total_profit!: number;

  @Type(() => Number)
  @IsNumber({ maxDecimalPlaces: 2 })
  grand_total!: number;

  @IsOptional()
  @IsString()
  lead_time!: string | null;

  @IsOptional()
  @IsDateString()
  created_at!: string | null;

  @IsOptional()
  @IsDateString()
  updated_at!: string | null;

  @IsUUID()
  proposal_id!: string;

  @IsEnum(ProposalPricingSummaryStatus)
  status!: ProposalPricingSummaryStatus;
}

export class ProposalsDto implements ProposalsResponseInterface {

  @IsUUID()
  id!: string;

  @IsString()
  project_name!: string;

  @IsOptional()
  @IsString()
  project_code!: string | null;

  @IsEnum(ProposalCompanies)
  company!: ProposalCompanies;

  @IsEnum(CategoryTypes)
  category!: CategoryTypes;

  @IsEnum(ProductCategory)
  product_category!: ProductCategory;

  @ValidateNested()
  @Type(() => CompanyDto)
  company_ref!: CompanyDto;

  @IsDateString()
  selected_date!: string;

  @IsOptional()
  @IsString()
  customer_ref!: string | null;

  @IsOptional()
  @IsString()
  quotation_number!: string | null;

  @IsEnum(ProposalPaymentTerms)
  payment_terms!: ProposalPaymentTerms;

  @IsEnum(ProposalValidity)
  quote_validity!: ProposalValidity;

  @IsEnum(ProposalWarranty)
  warranty!: ProposalWarranty;

  @IsOptional()
  @IsString()
  company_remarks!: string | null;

  @IsEnum(ProposalStatus)
  status!: ProposalStatus;

  @IsOptional()
  @IsDateString()
  created_at!: string | null;

  @IsOptional()
  @IsDateString()
  updated_at!: string | null;

  // Nested Relations
  @IsOptional()
  @ValidateNested({ each: true })
  @Type(() => ProposalAttachmentsDto)
  proposal_attachments!: ProposalAttachmentsDto[] | null;

  @IsOptional()
  @ValidateNested({ each: true })
  @Type(() => ProposalItemsDto)
  proposal_items!: ProposalItemsDto[] | null;

  @IsOptional()
  @ValidateNested()
  @Type(() => ProposalPricingSummaryDto)
  proposal_pricing_summary!: ProposalPricingSummaryDto | null;

  // Users
  @IsOptional()
  @ValidateNested()
  @Type(() => MiniUsersDto)
  created_by!: MiniUsersDto | null;

  @IsOptional()
  @ValidateNested()
  @Type(() => MiniUsersDto)
  updated_by!: MiniUsersDto | null;
}